import { BundleTag } from '@inkitt/galatea-common/types/payment-backend/entities/payment-bundle';
import { z } from 'zod';

// These schemas were previously imported from @inkitt/galatea-common but are redefined here
// because that package is compiled against Zod v3 and its type declarations are incompatible with Zod v4.
const upsellOfferItem = z.object({
  type: z.literal('upsell'),
  config: z.object({
    basePoints: z.number(),
    maxTimesAllowedPurchase: z.number(),
    upsellForBundleIds: z.array(z.string()),
  }),
});

const abandonedCartOfferItem = z.object({
  type: z.literal('abandonedCart'),
  config: z.object({
    basePoints: z.number(),
    maxTimesAllowedPurchase: z.number(),
    showForBundleIds: z.array(z.string()),
  }),
});

const countdownOfferItem = z.object({
  type: z.literal('countdownOffer'),
  config: z.object({
    basePoints: z.number(),
    maxTimesAllowedPurchase: z.number(),
    minUniqueCountdownViews: z.number(),
    daysToShowOffer: z.number(),
    offerShowPerDay: z.number(),
    minSecondsBetweenViews: z.number().optional(),
  }),
});

const pointsOfferDetails = z.discriminatedUnion('type', [upsellOfferItem, abandonedCartOfferItem, countdownOfferItem]);

export const PointsBundle = z.object({
  id: z.string(),
  adjustEventId: z.string(),
  tiers: z.number(),
  title: z.string(),
  description: z.string(),
  savings: z.number().optional(),
  bonusPoints: z.number().optional(),
  /**
   * Headline bonus percentage for packages sold as "base + free points" (DEV-6331). Kept as data
   * instead of derived from `bonusPoints / (tiers - bonusPoints)`: the advertised figures are round
   * marketing numbers and do not always match the arithmetic — the 34+42 package is sold as 120%
   * where the ratio is 123.5%. Distinct from `savings`, which renders as "save X%" (a discount).
   */
  bonusPercent: z.number().optional(),
  chapters: z.number().optional(),
  isRecommended: z.boolean().optional(),
  isMostPopular: z.boolean().optional(),
  isGoldenHourOffer: z.boolean().optional(),
  /**
   * Marks the one-off cheap starter pack (Audiobooks Plus trial pack, DEV-6331 follow-up). The pack
   * ships in the catalogue for everyone — the shop is server-driven and knows nothing about AB tests
   * — so the client MUST pull it out of the normal list and render it only to eligible users. A flag
   * on the bundle rather than a hardcoded SKU: the id can change without touching the UI.
   */
  isTrialPack: z.boolean().optional(),
  offerDetails: pointsOfferDetails.optional(),
  upsellConfig: upsellOfferItem.optional(),
  abandonedCartConfig: abandonedCartOfferItem.optional(),
  tags: z.array(z.literal(BundleTag.Default)).optional(),
  markupPrice: z.string().optional(),
});

export type PointsBundle = z.infer<typeof PointsBundle>;
